import {
  DLSelect,
  hierarchyContainer,
  // eslint-disable-next-line no-unused-vars
  hierarchyTreeModel,
} from '@wallaby-ui/core';
import {includes, isArray, isEqual, map, some} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {DynamicFilter, Filter, FILTER_TYPE_MULTI_SELECT} from 'src/js/lib/filtering';
import {FilterNode as FilterNodeModel} from 'src/js/model/filters_config';

import FilterNodeSelectRemoveAll from './FilterNodeSelectRemoveAll';
import FilterNodeSelectRemoveItem from './FilterNodeSelectRemoveItem';
import styles from './FilterNodeTypeHierarchyMultiSelect.less';
import {I18N as SelectI18N} from './FilterNodeTypeSelectContainer';
import {FilterNodeTypeWrapper} from './FilterNodeTypeWrapper';

class FilterNodeTypeHierarchyMultiSelect extends React.Component {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.hierarchyNodeClick = this.hierarchyNodeClick.bind(this);
  }

  componentDidMount() {
    const {selectedFilter} = this.props;
    if (selectedFilter) {
      this.loadAutocompleteValues();
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    if (isEqual(this.props, nextProps) && isEqual(this.state, nextState) && isEqual(this.context, nextContext)) {
      return false;
    }
    return true;
  }

  componentDidUpdate(prevProps) {
    const {selectedFilter} = this.props;
    if (selectedFilter && (!prevProps.selectedFilter || !isEqual(selectedFilter.getValue(), prevProps.selectedFilter.getValue()))) {
      this.loadAutocompleteValues();
    }
  }

  handleChange(value) {
    const values = map(value, (item) => item.value);
    this.changeFilter(values);
  }

  getFilter(value) {
    const {node} = this.props;
    const MultiSelectFilter = DynamicFilter.getFilterClass(FILTER_TYPE_MULTI_SELECT);
    return new MultiSelectFilter({
      node,
      type: FILTER_TYPE_MULTI_SELECT,
      property: node.getName(),
      value,
    });
  }

  getValue() {
    const {selectedAutocompleteValues} = this.props;
    return selectedAutocompleteValues;
  }

  loadAutocompleteValues() {
    const {autocompleteSetValues, selectedAutocompleteValues, selectedFilter} = this.props;
    // get own (id) values of selectedAutocompleteValues array of AutocompleteMappedOption objects
    const ownAutocompleteValues = (selectedAutocompleteValues || []).map((autocompleteMappedOption) => autocompleteMappedOption.getValue());
    // check if selectedAutocompleteValues has the same number of values as the selectedFilter
    // or if some value of selected filter is missing in ownAutocompleteValues
    if (
      !selectedAutocompleteValues ||
      selectedAutocompleteValues.length !== selectedFilter.getValue().length ||
      some(selectedFilter.getValue(), (filterItemValue) => !includes(ownAutocompleteValues, filterItemValue))
    ) {
      // (fetch and) set new values if some labels are missing
      autocompleteSetValues(selectedFilter.getValue());
    }
  }

  changeFilter(value) {
    const {changeFilter, removeFilter} = this.props;
    const filter = this.getFilter(value);
    if (value.length > 0) {
      changeFilter(filter);
    } else {
      removeFilter(filter);
    }
  }

  /**
   * @param {hierarchyTreeModel.HierarchyTreeNode} node The clicked hierarchy node.
   */
  hierarchyNodeClick(node) {
    const {selectedFilter} = this.props;
    const currentValue = selectedFilter && selectedFilter.getValue() ? selectedFilter.getValue() : [];
    if (node && !node.getDocumentId()) {
      const addedValue = node.getPath();
      if (currentValue.indexOf(addedValue) === -1) {
        // add unless it is not already there
        const values = [...(isArray(currentValue) ? currentValue : [currentValue]), node.getPath()];
        this.changeFilter(values);
      }
    }
  }

  render() {
    const {autocomplete, config, hierarchyStateId, inputRef, isOpened, multi, node, toggleOpened, removeFilter, selectedFilter, setFocus} =
      this.props;
    const filter = this.getFilter();
    const value = this.getValue();

    return (
      <FilterNodeTypeWrapper
        filter={filter}
        isOpened={isOpened}
        node={node}
        removeFilter={removeFilter}
        setFocus={setFocus}
        selectedFilter={selectedFilter}
        toggleOpened={toggleOpened}
      >
        <div className={'dlFilterNodeContentInput'}>
          {multi ? (
            <DLSelect
              isAsync
              inputRef={inputRef}
              name={node.getName()}
              id={node.getName()}
              selectedValue={value}
              isMulti
              loadOptions={autocomplete}
              noOptionsText={SelectI18N.NO_OPTIONS.id}
              placeholderText={SelectI18N.PLACEHOLDER.id}
              tabIndex={isOpened ? 0 : -1}
              onChange={this.handleChange}
              components={{
                ClearIndicator: FilterNodeSelectRemoveAll,
                MultiValueRemove: FilterNodeSelectRemoveItem,
              }}
            />
          ) : null}
          <ul className={styles.dlHierarchyTree}>
            <hierarchyContainer.HierarchyContainer
              hierarchyStateId={hierarchyStateId}
              rootLabel={node.getName()}
              hierarchyTypeId={node.getName()}
              onNodeClick={this.hierarchyNodeClick}
              childrenByParentUrl={
                node.isSecured() ? config.service.url.securedHierarchyChildrenByParent : config.service.url.hierarchyChildrenByParent
              }
              childrenByTypeUrl={
                node.isSecured() ? config.service.url.securedHierarchyChildrenByType : config.service.url.hierarchyChildrenByType
              }
              isTogglingSeparated
              showRoot={false}
              tabIndex={isOpened ? 0 : -1}
            />
          </ul>
        </div>
      </FilterNodeTypeWrapper>
    );
  }
}

FilterNodeTypeHierarchyMultiSelect.displayName = 'FilterNodeTypeHierarchyMultiSelect';

FilterNodeTypeHierarchyMultiSelect.propTypes = {
  autocomplete: PropTypes.func.isRequired,
  autocompleteSetValues: PropTypes.func,
  config: PropTypes.objectOf(PropTypes.any).isRequired,
  hierarchyStateId: PropTypes.string.isRequired,
  inputRef: PropTypes.func.isRequired,
  isOpened: PropTypes.bool,
  multi: PropTypes.bool,
  node: PropTypes.instanceOf(FilterNodeModel).isRequired,
  changeFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  selectedAutocompleteValues: PropTypes.arrayOf(PropTypes.any),
  selectedFilter: PropTypes.instanceOf(Filter),
  setFocus: PropTypes.func.isRequired,
  toggleOpened: PropTypes.func.isRequired,
};

FilterNodeTypeHierarchyMultiSelect.defaultProps = {
  autocompleteSetValues: undefined,
  isOpened: false,
  multi: false,
  selectedAutocompleteValues: undefined,
  selectedFilter: undefined,
};

export default FilterNodeTypeHierarchyMultiSelect;
